import { ApolloError } from '@apollo/client/errors';
import { Button } from '@vechaiui/react';
import { Form, FormikProps } from 'formik';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useLocalization } from '@fluent/react';
import { useNavigate } from 'react-router-dom';
import { useRef, useState } from 'react';

import AppContainerLayout from '#/layouts/AppContainerLayout';
import Dialog from '#/components/controls/Dialog';
import Formik from '#/components/forms/Formik';
import RequestForQuotationForm from '#/components/forms/RequestForQuotationForm';
import TripSummaryPanel from '#/components/panels/TripSummaryPanel';
import { ApiErrorBlock, ErrorBlockFormik } from '#/components/forms/ErrorBlock';
import { PrivateRoute } from '#/constants/page-route';
import { RFQ_INITIAL_VALUES } from '#/constants/form-initial-values';
import {
  CreatePaymentDocument,
  CreateRequestForQuotationInput,
  CreateRfqDocument,
  GetOrdersByOfferIdDocument,
  GetOrdersByOrderNumberDocument,
  GetOrdersCountByStatusesDocument,
  GetOrdersDocument,
  PaymentMethod,
  RequestForQuotation,
  RequestRouteDocument,
  RouteLeg,
} from '#/__generated__/graphql/graphql';
import { rfqSchema } from '#/constants/validation-schema';
import { sanitizeRFQFormData } from '#/utils/sanitize-data';

const RequestForQuotationPage = () => {
  const formRef = useRef<FormikProps<any>>(null);
  const navigate = useNavigate();
  const { l10n } = useLocalization();

  const [createRfqMutation] = useMutation(CreateRfqDocument);
  const [executeGetOrdersByOfferIdQuery] = useLazyQuery(GetOrdersByOfferIdDocument);
  const [createPaymentMutation] = useMutation(CreatePaymentDocument, {
    refetchQueries: [
      GetOrdersDocument,
      GetOrdersByOrderNumberDocument,
      GetOrdersCountByStatusesDocument,
    ],
  });
  const [requestRouteMutation] = useMutation(RequestRouteDocument);

  const [apiErrors, setApiErrors] = useState<{ message: string }[] | undefined>();
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isRfqPriceError, setRfqPriceError] = useState(false);

  const composePayment = (input: CreateRequestForQuotationInput) => {
    // 1. Create rfq;
    // 2. Get order id;
    // 3. Make payment
    return createRfqMutation({ variables: { input } })
      .then(async (res) => {
        if (res.errors) throw res.errors[0];
        const rfqId = res.data?.createRequestForQuotation?.node.id;
        if (!rfqId) {
          throw new Error('No RFQ ID');
        }

        return executeGetOrdersByOfferIdQuery({
          variables: { offerId: rfqId },
        });
      })
      .then((res) => {
        if (res.error) throw res.error;
        const order = res.data?.orders?.edges[0].node;
        if (!order) {
          throw new Error('No order');
        }
        return createPaymentMutation({
          variables: {
            input: {
              orderId: order.id,
              // paymentMethod: PaymentMethod.Fpx,
              paymentMethod: PaymentMethod.CreditTerms,
              priceSpecification: {
                priceCurrency: order.orderTotal.priceCurrency,
                price: order.orderTotal.price,
              },
            },
          },
        });
      })
      .then((res) => {
        if (res.errors) throw res.errors[0];
        const orderNumber = res.data?.createPayment?.node.order.orderNumber;
        if (!orderNumber) {
          throw new Error('No order number available.');
        }
        return orderNumber;
        // const paymentUrl = res.data?.createPayment?.node.paymentUrl;
        // if (!paymentUrl) {
        //   throw new Error('No payment URL');
        // }
        // return paymentUrl;
      });
  };

  const handleContactUsPressed = async () => {
    const routeLeg: RouteLeg = formRef.current?.values.routeLegs[0];
    await requestRouteMutation({
      variables: {
        input: {
          routeLegs: [
            {
              endLocation: routeLeg.endLocation,
              startLocation: routeLeg.startLocation,
            },
          ],
        },
      },
    });
    alert(
      'Thank you for choosing MOVE. We will get back to you on your order as soon as possible.',
    );
    formRef.current?.resetForm();
    // TODO: Send message to MOVE team.
    // navigate(PrivateRoute.OrderList);
  };
  const handleRfqPriceError = (error: ApolloError | undefined) => {
    setRfqPriceError(error?.message.startsWith('No rates found') ? true : false);
  };

  return (
    <AppContainerLayout>
      <Formik
        innerRef={formRef}
        initialValues={RFQ_INITIAL_VALUES}
        onSubmit={async (formData, { resetForm }) => {
          try {
            const form = sanitizeRFQFormData(formData);
            const orderNumber = await composePayment(form);
            resetForm();
            navigate(`${PrivateRoute.PaymentConfirm}?order_number=${orderNumber}`);
            // window.location.href = paymentUrl;
          } catch (err: any) {
            if (err.errors) {
              setApiErrors(err.errors);
            } else if (err.networkError?.result.errors) {
              setApiErrors(err.networkError.result.errors);
            } else {
              setApiErrors([err]);
            }
          } finally {
            setConfirmDialogOpen(false);
          }
        }}
        validationSchema={rfqSchema}
      >
        {(formikProps) => (
          <>
            <Form>
              <div className="flex">
                <div className="flex-1 p-8 bg-white 2xl:p-12 2xl:pb-8">
                  <header className="flex items-center justify-between mb-8 border-b border-gray-300">
                    <h2 className="mb-8 text-3xl font-bold text-black">
                      {l10n.getString('trip-request')}
                    </h2>
                    {/* <div className="mb-8">
                      <Stepper currentStep={1} totalStep={5} />
                    </div> */}
                  </header>
                  <RequestForQuotationForm />
                </div>
                <div className="w-[375px] 2xl:w-[425px]">
                  <div className="sticky top-0 p-6 pt-8 2xl:p-8 2xl:pt-12">
                    <TripSummaryPanel
                      onError={handleRfqPriceError}
                      rfq={formikProps.values as RequestForQuotation}
                    />
                    <div className="mt-6 space-y-2">
                      {!formikProps.isValid && formikProps.submitCount > 0 && <ErrorBlockFormik />}
                      {apiErrors && <ApiErrorBlock errors={apiErrors} />}
                    </div>

                    <footer className="mt-6">
                      {isRfqPriceError ? (
                        <Button
                          className="w-full"
                          color="primary"
                          loading={formikProps.isSubmitting}
                          onClick={handleContactUsPressed}
                          size="xl"
                          type="button"
                          variant="solid"
                        >
                          {l10n.getString('contact-us')}
                        </Button>
                      ) : (
                        <Button
                          className="w-full"
                          color="primary"
                          // disabled={!formikProps.isValid}
                          loading={formikProps.isSubmitting}
                          onClick={() => {
                            setConfirmDialogOpen(true);
                          }}
                          size="xl"
                          type="button"
                          variant="solid"
                        >
                          {`${l10n.getString('confirm')} ${l10n.getString('trip')}`}
                        </Button>
                      )}
                    </footer>
                  </div>
                </div>
              </div>
            </Form>

            {isConfirmDialogOpen && (
              <Dialog
                delayConfirmButton
                onConfirm={async () => {
                  await formikProps.validateForm();
                  setConfirmDialogOpen(false);
                  formikProps.submitForm();
                }}
                onClose={() => {
                  setConfirmDialogOpen(false);
                }}
                isOpen={isConfirmDialogOpen}
                title={l10n.getString('confirm-trip')}
              >
                <p className="text-black font-semibold">{l10n.getString('confirm-trip-message')}</p>
              </Dialog>
            )}
          </>
        )}
      </Formik>
    </AppContainerLayout>
  );
};

export default RequestForQuotationPage;
